import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { clothingDescriptions } from '../data/clothingDescriptions';
import { clothingSpecs } from '../data/clothingSpecs';
import { getStoreImagesForName } from '../data/storeImages';
import { updatedProductData, ProductData, findProductBySlug } from '../data/productData';
import { getTranslatedSpecifications, translateSpecification } from '../data/specificationsTranslations';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../components/Navbar';

// Dark Luxury Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(212, 175, 55, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(212, 175, 55, 0.6);
  }
`;

// Main Container
// Store Navigation Bar
const NavigationBar = styled.nav`
  position: relative;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid #f5f5f5;
  padding: 1rem 0;
  transition: all 0.3s ease;
  margin-top: 0;
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 300;
  letter-spacing: -0.02em;
  color: #1a1a1a;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #d4af37;
    transform: scale(1.05);
  }
`;

const NavCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex: 1;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }
`;

const SearchBar = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 1024px) {
    width: 200px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 25px;
  background: #f5f5f5;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
  color: #888888;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  background-image: url('/images/logos/search.webp');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  filter: brightness(0.5);
`;

const CategoryDropdown = styled.div`
  position: relative;
`;

const CategoryDropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 200px;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  margin-top: 0.5rem;
`;

const CategoryDropdownItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  color: #1a1a1a;
  
  &:hover {
    background: #f5f5f5;
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const CategoryDropdownButton = styled.button`
  background: none;
  border: none;
  color: #1a1a1a;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const NavButton = styled.button`
  background: none;
  border: 1px solid #e0e0e0;
  outline: none;
  color: #1a1a1a;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: #f5f5f5;
    border-color: #d4af37;
    color: #d4af37;
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
`;

const CartButton = styled.button`
  position: relative;
  background: none;
  border: none;
  color: #1a1a1a;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  width: 24px;
  height: 24px;
  background-image: url('/images/logos/pngwing.com (11).webp');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const CartBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #d4af37;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductDetailContainer = styled.div`
  min-height: 100vh;
  background: #0d0d0d;
  color: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Breadcrumb
const Breadcrumb = styled.nav`
  margin-bottom: 2rem;
  font-size: 0.875rem;
  color: #666666;
  
  a {
    color: #666666;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: #d4af37;
    }
  }
`;

// Product Layout
const ProductLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

// Image Gallery
const ImageGallery = styled.div`
  position: sticky;
  top: 2rem;
  
  @media (max-width: 1024px) {
    position: static;
  }
`;

const MainImage = styled.div`
  position: relative;
  aspect-ratio: 3248 / 4330; /* 3:4 portrait, same as list cards */
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
    border-radius: 8px;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${MainImage}:hover & {
    transform: scale(1.05);
  }
`;

const ImageThumbnails = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding: 0.5rem 0;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    padding: 0.25rem 0;
    
    /* Hide scrollbar but keep functionality */
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Thumbnail = styled.button<{ active: boolean }>`
  min-width: 80px;
  max-width: 80px;
  height: 80px;
  border: 2px solid ${props => props.active ? '#d4af37' : '#333333'};
  border-radius: 8px;
  overflow: hidden;
  background: #1a1a1a;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &:hover {
    border-color: #d4af37;
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    min-width: 60px;
    max-width: 60px;
    height: 60px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    min-width: 50px;
    max-width: 50px;
    height: 50px;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    border: none;
    outline: none;
  }
`;

// Product Info
const ProductInfo = styled.div`
  padding: 1rem 0;
  
  @media (max-width: 768px) {
    padding: 0.5rem 0;
  }
`;

const ProductBrand = styled.div`
  font-size: 0.875rem;
  color: #d4af37;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const ProductName = styled.h1`
  font-size: 3rem;
  font-weight: 200;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: #ffffff;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ProductDetails = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem 0;
  border-top: 1px solid #333;
  border-bottom: 1px solid #333;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #d4af37;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span`
  font-size: 1rem;
  color: #ffffff;
  font-weight: 400;
`;

const ProductPrice = styled.div`
  font-size: 2.5rem;
  font-weight: 300;
  margin-bottom: 1.5rem;
  color: #d4af37;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;


const ProductDescription = styled.div`
  margin-bottom: 2rem;
  
  p {
    color: #cccccc;
    line-height: 1.6;
    margin-bottom: 1rem;
    font-size: 1rem;
  }
`;

const ProductOriginalPrice = styled.div`
  font-size: 1.5rem;
  color: #888888;
  margin-bottom: 1rem;
`;

const SizeGrid = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const SizeOption = styled.button<{ selected: boolean }>`
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.selected ? '#d4af37' : '#333333'};
  background: ${props => props.selected ? '#d4af37' : 'transparent'};
  color: ${props => props.selected ? '#0d0d0d' : '#ffffff'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  font-weight: 500;
  
  &:hover {
    border-color: #d4af37;
    background: ${props => props.selected ? '#d4af37' : 'rgba(212, 175, 55, 0.1)'};
  }
`;

const AddToCartButton = styled.button`
  width: 100%;
  background: #d4af37;
  color: #0d0d0d;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  
  &:hover {
    background: #b8941f;
    transform: translateY(-2px);
  }
`;

const AskQuestionButton = styled.button`
  width: 100%;
  background: transparent;
  color: #d4af37;
  border: 2px solid #d4af37;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  
  &:hover {
    background: rgba(212, 175, 55, 0.1);
    transform: translateY(-2px);
  }
`;

// Question Modal Styled Components
const QuestionModalContent = styled(motion.div)`
  background: #ffffff;
  border-radius: 16px;
  padding: 2.5rem;
  max-width: 600px;
  width: 90%;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
`;

const QuestionModalTitle = styled.h2`
  font-size: 1.75rem;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

const QuestionModalSubtitle = styled.p`
  font-size: 1rem;
  color: #666;
  margin-bottom: 2rem;
  font-weight: 500;
`;

const QuestionForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
`;

const FormInput = styled.input`
  padding: 0.875rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  padding: 0.875rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  background: #d4af37;
  color: #0d0d0d;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #b8941f;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  padding: 2rem;
  background: #e8f5e9;
  border: 2px solid #4caf50;
  border-radius: 12px;
  color: #2e7d32;
  font-size: 1.1rem;
  text-align: center;
  font-weight: 500;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  background: transparent;
  color: #d4af37;
  border: 1px solid #d4af37;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  
  &:hover {
    background: rgba(212, 175, 55, 0.1);
    transform: translateY(-2px);
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: -2rem;
  right: 0;
  background: none;
  border: none;
  color: #ffffff;
  font-size: 2rem;
  cursor: pointer;
  padding: 0.5rem;
  
  &:hover {
    color: #d4af37;
  }
`;

const ModalPrevButton = styled.button`
  position: absolute;
  left: -3rem;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: #ffffff;
  font-size: 2rem;
  cursor: pointer;
  padding: 1rem;
  border-radius: 50%;
  
  &:hover {
    background: rgba(212, 175, 55, 0.8);
  }
`;

const ModalNextButton = styled.button`
  position: absolute;
  right: -3rem;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: #ffffff;
  font-size: 2rem;
  cursor: pointer;
  padding: 1rem;
  border-radius: 50%;
  
  &:hover {
    background: rgba(212, 175, 55, 0.8);
  }
`;

// Product Options
const ProductOptions = styled.div`
  margin-bottom: 2rem;
`;

const OptionGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const OptionLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;


const ColorOptions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ColorOption = styled.button<{ color: string; selected: boolean }>`
  width: 50px;
  height: 50px;
  border: 3px solid ${props => props.selected ? '#d4af37' : '#333333'};
  background: ${props => props.color};
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #d4af37;
    transform: scale(1.1);
  }
`;

// Quantity Selector
const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const QuantityLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #333333;
  border-radius: 8px;
  overflow: hidden;
  background: #1a1a1a;
`;

const QuantityButton = styled.button`
  width: 50px;
  height: 50px;
  border: none;
  background: #333333;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.25rem;
  
  &:hover {
    background: #d4af37;
  }
`;

const QuantityInput = styled.input`
  width: 80px;
  height: 50px;
  border: none;
  text-align: center;
  font-size: 1rem;
  background: #1a1a1a;
  color: #ffffff;
  
  &:focus {
    outline: none;
  }
`;

// Action Buttons
const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  background: #d4af37;
  color: #0d0d0d;
  border: none;
  padding: 1.25rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  
  &:hover {
    background: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(212, 175, 55, 0.3);
    animation: ${glow} 2s infinite;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  background: transparent;
  color: #ffffff;
  border: 1px solid #d4af37;
  padding: 1.25rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  
  &:hover {
    background: #d4af37;
    color: #0d0d0d;
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
  }
`;

// Tabs Section
const TabsSection = styled.section`
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 1px solid #333333;
`;

const TabsHeader = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 2rem;
  border-bottom: 1px solid #333333;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 1rem 2rem;
  border: none;
  background: none;
  color: ${props => props.active ? '#d4af37' : '#666666'};
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 2px solid ${props => props.active ? '#d4af37' : 'transparent'};
  
  &:hover {
    color: #d4af37;
  }
`;

const TabContent = styled.div`
  padding: 2rem 0;
  color: #cccccc;
  line-height: 1.6;
  
  h3 {
    color: #ffffff;
    margin-bottom: 1rem;
    font-size: 1.25rem;
  }
  
  p {
    margin-bottom: 1rem;
  }
  
  ul {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
`;

// Related Products
const RelatedProducts = styled.section`
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 1px solid #333333;
`;

const RelatedTitle = styled.h2`
  font-size: 2rem;
  font-weight: 200;
  margin-bottom: 2rem;
  text-align: center;
  color: #ffffff;
  letter-spacing: -0.02em;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
`;

const RelatedCard = styled(motion.div)`
  background: #1a1a1a;
  border: 1px solid #333333;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #d4af37;
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  }
`;

const RelatedImage = styled.div`
  height: 250px;
  background: #0d0d0d;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  ${RelatedCard}:hover & img {
    transform: scale(1.05);
  }
`;

const RelatedInfo = styled.div`
  padding: 1.5rem;
`;

const RelatedName = styled.h3`
  font-size: 1.125rem;
  font-weight: 400;
  margin-bottom: 0.5rem;
  color: #ffffff;
  line-height: 1.3;
`;

const RelatedPrice = styled.div`
  font-size: 1.25rem;
  font-weight: 300;
  color: #d4af37;
`;


// Social Share
const SocialShare = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const SocialButton = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid #333333;
  background: transparent;
  color: #666666;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: #d4af37;
    color: #d4af37;
    transform: scale(1.1);
  }
`;

// Image Modal/Lightbox
const ImageModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;


const ModalClose = styled.button`
  position: absolute;
  top: -45px;
  right: 0;
  background: none;
  border: none;
  color: #ffffff;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 300;
  transition: all 0.3s ease;
  
  &:hover {
    color: #d4af37;
    transform: scale(1.2);
  }
  
  @media (max-width: 768px) {
    top: -50px;
    right: 10px;
    width: 28px;
    height: 28px;
    font-size: 1.2rem;
  }
`;

const ModalNavigation = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const ModalNavButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: #d4af37;
    color: #d4af37;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.2);
      color: #ffffff;
    }
  }
`;

// Product Data - Same as Store page
interface Product {
  id: number;
  name: string;
  slug: string;
  brand: string;
  price: string;
  originalPrice?: string;
  newPrice: number;
  salePrice: number;
  condition: string;
  image: string;
  images?: string[];
  category: string;
  badge: 'new' | 'sale' | 'limited' | null;
  inStock: boolean;
  isSecondHand?: boolean;
  size?: string;
  description?: string;
  specifications?: string[];
}

// Kondisyon rating fonksiyonu
const getConditionRating = (condition: string): number => {
  switch (condition) {
    case 'Like New':
      return 10;
    case 'Very Good':
      return 8;
    case 'Good':
      return 6;
    case 'Old but Good':
      return 4;
    default:
      return 5;
  }
};

// Rating görsel bileşeni
const ConditionRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const RatingStars = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const Star = styled.span<{ $filled: boolean }>`
  color: ${props => props.$filled ? '#d4af37' : '#333333'};
  font-size: 1.2rem;
  transition: color 0.3s ease;
`;

const RatingNumber = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #d4af37;
  margin-left: 0.5rem;
`;

const RatingText = styled.span`
  font-size: 0.875rem;
  color: #cccccc;
  margin-left: 0.5rem;
`;

// Shipping Information
const ShippingInfo = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #1a1a1a;
  border: 1px solid #333333;
  border-radius: 12px;
`;

const ShippingTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #d4af37;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ShippingDimensions = styled.div`
  margin-bottom: 1.5rem;
`;

const DimensionsTitle = styled.h4`
  font-size: 1rem;
  font-weight: 500;
  color: #ffffff;
  margin-bottom: 0.75rem;
`;

const DimensionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DimensionItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #333333;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DimensionLabel = styled.span`
  font-size: 0.875rem;
  color: #cccccc;
  font-weight: 500;
`;

const DimensionValue = styled.span`
  font-size: 0.875rem;
  color: #ffffff;
  font-weight: 600;
`;

const ShippingCompanies = styled.div`
  margin-bottom: 1.5rem;
`;

const CompaniesTitle = styled.h4`
  font-size: 1rem;
  font-weight: 500;
  color: #ffffff;
  margin-bottom: 0.75rem;
`;

const CompanyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CompanyItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #0d0d0d;
  border: 1px solid #333333;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #d4af37;
    background: rgba(212, 175, 55, 0.05);
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const CompanyName = styled.span`
  font-size: 0.875rem;
  color: #ffffff;
  font-weight: 500;
`;

const DeliveryTime = styled.span`
  font-size: 0.75rem;
  color: #cccccc;
  font-weight: 400;
`;

const ShippingPrice = styled.span`
  font-size: 0.875rem;
  color: #d4af37;
  font-weight: 600;
`;

const ShippingNote = styled.p`
  font-size: 0.75rem;
  color: #888888;
  margin: 0;
  font-style: italic;
  text-align: center;
`;

// CURRENT PRODUCT-IMAGE MAPPING (PRESERVED):
/*
SHOES (productNumber -> fileIndex mapping):
1. Beymen Suede Penny Loafers → product1 (main: product1-3.jpeg, variants: product1.jpeg, product1-1.jpeg, product1-2.jpeg, product1-4.jpeg)
2. Saint Laurent High-Top Sneakers → product2 (main: product2-4.jpeg, variants: product2.jpeg, product2-1.jpeg, product2-2.jpeg, product2-3.jpeg)
3. New Balance Retro Runner → product4 (variants: product4.jpeg, product4-1.jpeg, product4-2.jpeg, product4-3.jpeg)
4. Versace Jeans Couture Logo Sneakers → product5 (variants: product5.jpeg, product5-1.jpeg, product5-2.jpeg, product5-3.jpeg)
5-15. Other shoes → product6-product16 (3 variants each)
16. Jimmy Choo "JC Logo" Sneakers → product18 images (product18.jpeg, product18-1.jpeg, product18-2.jpeg, product18-3.jpeg)
17. adidas YEEZY Knit Sneaker → product19 images (product19.jpeg, product19-1.jpeg, product19-2.jpeg, product19-3.jpeg, product19-4.jpeg)
18. Generic product → product18 (3 variants)
19. Jimmy Choo variant → product18 images

CLOTHING (productNumber -> productNumber mapping with specific overrides):
- Most products use product{productNumber}.webp
- Special mappings preserved in clothing section
*/

// Utility function to create URL-friendly slugs from product names
const createSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens and spaces
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

const generateProducts = (): Product[] => {
  const products: Product[] = [];
  
  // Convert updatedProductData to Product format
  updatedProductData.forEach((productData: ProductData) => {
    const storeImgs = getStoreImagesForName(productData.name, productData.category);
    const folderName = productData.category === 'Shoes' ? 'Ayakkabı' : 'Kıyafet';
    const resolvedImages = storeImgs.length > 0 ? storeImgs : [`/images/products/${folderName}/product${productData.id}.webp`];
    
    products.push({
      id: productData.id,
      name: productData.name,
      slug: productData.slug,
      brand: productData.brand,
      price: `₺${productData.salePrice.toLocaleString()}`,
      originalPrice: productData.newPrice > 0 ? `$${productData.newPrice.toLocaleString()}` : '',
      newPrice: productData.newPrice,
      salePrice: productData.salePrice,
      condition: productData.condition,
      image: resolvedImages[0],
      images: resolvedImages,
      category: productData.category,
      badge: Math.random() > 0.7 ? 'new' : Math.random() > 0.8 ? 'sale' : null,
      inStock: Math.random() > 0.1,
      isSecondHand: productData.isSecondHand,
      size: productData.size,
      description: productData.description,
      specifications: productData.specifications
    });
  });
  
  return products;
};

const allProducts = generateProducts();

const getProductData = (id: number) => {
  const product = allProducts.find(p => p.id === id);
  
  if (!product) {
    // Fallback for non-existent products
    return {
      id: id,
      name: 'Ürün Bulunamadı',
      brand: 'Bilinmiyor',
      price: '₺0',
      originalPrice: undefined,
      newPrice: 0,
      salePrice: 0,
      condition: 'Bilinmiyor',
      image: '/images/placeholder.jpg',
      images: ['/images/placeholder.jpg'],
      category: 'Clothing',
      badge: null,
      inStock: false,
      isSecondHand: false
    };
  }
  
  return product;
};

// Main ProductDetail Component
const ProductDetail: React.FC = () => {
  const { slug, category } = useParams<{ slug: string; category: string }>();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const [productData, setProductData] = useState<Product | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [questionForm, setQuestionForm] = useState({
    customer_name: '',
    customer_email: '',
    question: ''
  });
  const [questionSubmitting, setQuestionSubmitting] = useState(false);
  const [questionSuccess, setQuestionSuccess] = useState(false);
  const [reservedProductIds, setReservedProductIds] = useState<number[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle category dropdown toggle
  const toggleCategoryDropdown = () => {
    setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
  };

  // Handle category selection
  const handleCategorySelect = (category: string) => {
    setIsCategoryDropdownOpen(false);
    navigate(`/products?category=${category.toLowerCase()}`);
  };

  // Sepet bağlamını al
  const { addToCart, cartCount: contextCartCount, loading: cartLoading } = useCart();

  // Handle add to cart
  const handleAddToCart = async () => {
    if (!productData || cartLoading) return;
    
    // Rezerveli ürünleri sepete eklemeyi engelle
    if (reservedProductIds.includes(productData.id)) {
      alert('Bu ürün rezervelidir ve sepete eklenemez.');
      return;
    }
    
    try {
      setIsAddedToCart(true);
      await addToCart(productData);
      setTimeout(() => setIsAddedToCart(false), 2000);
    } catch (error) {
      setIsAddedToCart(false);
    }
  };

  const handleQuestionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productData) return;
    
    setQuestionSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${API_URL}/product-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_name: productData.name,
          customer_name: questionForm.customer_name,
          customer_email: questionForm.customer_email,
          question: questionForm.question
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setQuestionSuccess(true);
        setQuestionForm({
          customer_name: '',
          customer_email: '',
          question: ''
        });
        
        // 3 saniye sonra modal'ı kapat
        setTimeout(() => {
          setIsQuestionModalOpen(false);
          setQuestionSuccess(false);
        }, 3000);
      } else {
        alert(data.error || 'Soru gönderilirken bir hata oluştu');
      }
    } catch (error) {
      alert('Soru gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setQuestionSubmitting(false);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleNextImage = () => {
    if (productData?.images) {
      setSelectedImage((prev) => (prev + 1) % productData.images!.length);
    }
  };

  const handlePrevImage = () => {
    if (productData?.images) {
      setSelectedImage((prev) => (prev - 1 + productData.images!.length) % productData.images!.length);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Rezerveli ürünleri API'den çek
  useEffect(() => {
    const fetchReservedProducts = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        const response = await fetch(`${API_URL}/reserved-products`);
        const data = await response.json();
        if (data.reserved_product_ids) {
          setReservedProductIds(data.reserved_product_ids);
        }
      } catch (error) {
        // Hata sessizce yoksayılır
      }
    };

    fetchReservedProducts();
  }, []);

  useEffect(() => {
    if (!slug) return;
    
    const product = findProductBySlug(slug);
    if (!product) {
      setProductData(null);
      return;
    }
    
    const storeImgs = getStoreImagesForName(product.name, product.category);
    const folderName = product.category === 'Shoes' ? 'Ayakkabı' : 'Kıyafet';
    const resolvedImages = storeImgs.length > 0 ? storeImgs : [`/images/products/${folderName}/product${product.id}.webp`];
    
    const productData = {
      id: product.id,
      name: product.name,
      slug: product.slug,
      brand: product.brand,
      price: `₺${product.salePrice.toLocaleString()}`,
      originalPrice: product.newPrice > 0 ? `$${product.newPrice.toLocaleString()}` : '',
      newPrice: product.newPrice,
      salePrice: product.salePrice,
      condition: product.condition,
      image: resolvedImages[0],
      images: resolvedImages,
      category: product.category,
      badge: Math.random() > 0.7 ? 'new' as const : Math.random() > 0.8 ? 'sale' as const : null,
        inStock: Math.random() > 0.1,
      isSecondHand: product.isSecondHand,
      size: product.size,
      description: product.description,
      specifications: product.specifications
    };

      setProductData(productData);
  }, [slug, category]);

  if (!productData) {
    return (
      <ProductDetailContainer>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <h1>Ürün Bulunamadı</h1>
            <p>Üzgünüz, aradığınız ürün bulunamadı.</p>
          </div>
        </Container>
      </ProductDetailContainer>
    );
  }

  return (
    <ProductDetailContainer>
      <Helmet>
        <title>{productData.name} - Barkın Sayın Store</title>
        <meta name="description" content={`${productData.brand} ${productData.name} - ${productData.condition} durumunda, ₺${productData.salePrice.toLocaleString()} fiyatla satışta.`} />
        <meta property="og:title" content={`${productData.name} - Barkın Sayın Store`} />
        <meta property="og:description" content={`${productData.brand} ${productData.name} - ${productData.condition} durumunda, ₺${productData.salePrice.toLocaleString()} fiyatla satışta.`} />
        <meta property="og:image" content={productData.image} />
        <meta property="og:type" content="product" />
      </Helmet>
      
      {/* Store Navigation */}
      <NavigationBar>
        <NavContainer>
          <Logo onClick={() => navigate('/')}>STORE</Logo>
          
          <NavCenter>
            <SearchBar>
              <SearchIcon></SearchIcon>
              <SearchInput
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={handleSearchInputChange}
              />
            </SearchBar>
          </NavCenter>
                    <NavRight>
              <NavButton onClick={() => navigate('/')}>
                {language === 'tr' ? 'Ana Sayfa' : 'Home'}
              </NavButton>
              <NavButton onClick={() => navigate('/orders')}>
                {language === 'tr' ? 'Siparişlerim' : 'My Orders'}
              </NavButton>
              <div style={{ position: 'relative' }}>
                <NavButton onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}>
                  🌐 {language.toUpperCase()}
                </NavButton>
                {isLanguageDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    minWidth: '140px',
                    zIndex: 1000
                  }}>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setLanguage('tr'); setIsLanguageDropdownOpen(false); }} 
                      style={{
                        width: '100%',
                        padding: '12px 20px',
                        border: 'none',
                        background: language === 'tr' ? '#d4af37' : 'white',
                        color: language === 'tr' ? 'white' : '#1a1a1a',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: language === 'tr' ? '600' : '400',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => { if (language !== 'tr') e.currentTarget.style.background = '#f5f5f5'; }}
                      onMouseLeave={(e) => { if (language !== 'tr') e.currentTarget.style.background = 'white'; }}
                    >
                      🇹🇷 Türkçe
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setLanguage('en'); setIsLanguageDropdownOpen(false); }} 
                      style={{
                        width: '100%',
                        padding: '12px 20px',
                        border: 'none',
                        background: language === 'en' ? '#d4af37' : 'white',
                        color: language === 'en' ? 'white' : '#1a1a1a',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: language === 'en' ? '600' : '400',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => { if (language !== 'en') e.currentTarget.style.background = '#f5f5f5'; }}
                      onMouseLeave={(e) => { if (language !== 'en') e.currentTarget.style.background = 'white'; }}
                    >
                      🇬🇧 English
                    </button>
                  </div>
                )}
              </div>
              <CartButton onClick={() => navigate('/cart')}>
                {contextCartCount > 0 && <CartBadge>{contextCartCount}</CartBadge>}
                {cartLoading && <CartBadge>...</CartBadge>}
              </CartButton>
            </NavRight>
        </NavContainer>
      </NavigationBar>
      
      <Container>
        <BackButton onClick={() => navigate('/products')}>
          ← {language === 'tr' ? 'Ürünlere Dön' : 'Back to Products'}
        </BackButton>

          <ProductLayout>
            <ImageGallery>
            <MainImage onClick={openModal}>
                <ProductImage 
                src={productData.images?.[selectedImage] || productData.image}
                  alt={productData.name}
                  loading="eager"
                />
              </MainImage>
              
            {productData.images && productData.images.length > 1 && (
              <ImageThumbnails>
                {productData.images.map((image, index) => (
                  <Thumbnail
                    key={index}
                    active={selectedImage === index}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img src={image} alt={`${productData.name} ${index + 1}`} loading="lazy" />
                  </Thumbnail>
                ))}
              </ImageThumbnails>
            )}
            </ImageGallery>

            <ProductInfo>
              <ProductBrand>{productData.brand}</ProductBrand>
              <ProductName>{productData.name}</ProductName>
              
                <ProductDetails>
                  <DetailItem>
                <DetailLabel>Kondisyon:</DetailLabel>
                <DetailValue>
                  <ConditionRating>
                    <RatingStars>
                      {Array.from({ length: 5 }, (_, i) => {
                        const rating = getConditionRating(productData.condition);
                        const filled = i < Math.floor(rating / 2);
                        return (
                          <Star key={i} $filled={filled}>
                            ★
                          </Star>
                        );
                      })}
                    </RatingStars>
                    <RatingNumber>{getConditionRating(productData.condition)}/10</RatingNumber>
                    <RatingText>({productData.condition})</RatingText>
                  </ConditionRating>
                </DetailValue>
                  </DetailItem>
              {productData.size && (
                    <DetailItem>
                  <DetailLabel>{language === 'tr' ? 'Beden:' : 'Size:'}</DetailLabel>
                  <DetailValue>{productData.size}</DetailValue>
                    </DetailItem>
                  )}
              <DetailItem>
                <DetailLabel>{language === 'tr' ? 'Kategori:' : 'Category:'}</DetailLabel>
                <DetailValue>{productData.category === 'Shoes' ? (language === 'tr' ? 'Ayakkabı' : 'Shoes') : (language === 'tr' ? 'Giyim' : 'Clothing')}</DetailValue>
              </DetailItem>
                </ProductDetails>
              
              <ProductPrice>{productData.price} - {language === 'tr' ? 'Satış Fiyatı' : 'Sale Price'}</ProductPrice>
            {productData.originalPrice && (
              <ProductOriginalPrice>{productData.originalPrice} - {language === 'tr' ? 'Mağaza Fiyatı' : 'Store Price'}</ProductOriginalPrice>
            )}


            <ProductOptions>
              <OptionGroup>
                <OptionLabel>{language === 'tr' ? 'Beden Seçimi' : 'Size Selection'}</OptionLabel>
                <SizeGrid>
                  <SizeOption selected={true}>
                    {productData.size || (language === 'tr' ? 'Tek Beden' : 'One Size')}
                  </SizeOption>
                </SizeGrid>
              </OptionGroup>
            </ProductOptions>

            <AddToCartButton 
              onClick={handleAddToCart}
              disabled={reservedProductIds.includes(productData.id)}
              style={{
                opacity: reservedProductIds.includes(productData.id) ? 0.5 : 1,
                cursor: reservedProductIds.includes(productData.id) ? 'not-allowed' : 'pointer'
              }}
            >
              {reservedProductIds.includes(productData.id) 
                ? (language === 'tr' ? 'Rezerveli - Sepete Eklenemez' : 'Reserved - Cannot Add') 
                : isAddedToCart 
                ? (language === 'tr' ? 'Sepete Eklendi ✓' : 'Added to Cart ✓') 
                : (language === 'tr' ? 'Sepete Ekle' : 'Add to Cart')}
            </AddToCartButton>
            
            <AskQuestionButton onClick={() => setIsQuestionModalOpen(true)}>
              {language === 'tr' ? 'Ürün Hakkında Soru Sor' : 'Ask About Product'}
            </AskQuestionButton>

            </ProductInfo>
          </ProductLayout>

          {/* Back Button */}
          <BackButton onClick={() => navigate('/products')}>
            ← {language === 'tr' ? 'Ürünlere Dön' : 'Back to Products'}
          </BackButton>

          {/* Specifications Section */}
          <TabsSection>
            <TabsHeader>
              <TabButton active={true}>
                {language === 'tr' ? 'Ürün Özellikleri' : 'Product Specifications'}
              </TabButton>
            </TabsHeader>
            
            <TabContent>
              <div>
                {productData.description && (
                  <p style={{ marginBottom: '1.5rem', lineHeight: '1.6' }}>
                    {productData.description}
                  </p>
                )}
                
                <ul>
                  {productData.specifications && productData.specifications.length > 0 ? (
                    (() => {
                      // Önce özel çevirileri kontrol et
                      const translatedSpecs = getTranslatedSpecifications(productData.id, language as 'tr' | 'en');
                      if (translatedSpecs.length > 0) {
                        return translatedSpecs.map((spec: string, index: number) => (
                          <li key={index}>{spec}</li>
                        ));
                      }
                      // Özel çeviri yoksa genel çeviri kullan
                      return productData.specifications.map((spec: string, index: number) => (
                        <li key={index}>{translateSpecification(spec, language as 'tr' | 'en')}</li>
                      ));
                    })()
                  ) : (
                    <>
                      <li><strong>{language === 'tr' ? 'Marka:' : 'Brand:'}</strong> {productData.brand}</li>
                      <li><strong>{language === 'tr' ? 'Kategori:' : 'Category:'}</strong> {productData.category === 'Shoes' ? (language === 'tr' ? 'Ayakkabı' : 'Shoes') : (language === 'tr' ? 'Giyim' : 'Clothing')}</li>
                      <li><strong>{language === 'tr' ? 'Durum:' : 'Condition:'}</strong> {productData.condition}</li>
                      {productData.size && (
                        <li><strong>{language === 'tr' ? 'Beden:' : 'Size:'}</strong> {productData.size}</li>
                      )}
                      {productData.isSecondHand && (
                        <li><strong>{language === 'tr' ? 'İkinci El:' : 'Second Hand:'}</strong> {language === 'tr' ? 'Evet' : 'Yes'}</li>
                      )}
                      {productData.category === 'Shoes' && (
                        <>
                          <li><strong>{language === 'tr' ? 'Materyal:' : 'Material:'}</strong> {language === 'tr' ? 'Deri/Kumaş' : 'Leather/Fabric'}</li>
                          <li><strong>{language === 'tr' ? 'Taban:' : 'Sole:'}</strong> {language === 'tr' ? 'Kauçuk' : 'Rubber'}</li>
                          <li><strong>{language === 'tr' ? 'Renk:' : 'Color:'}</strong> {language === 'tr' ? 'Görseldeki gibi' : 'As shown in image'}</li>
                        </>
                      )}
                      {productData.category === 'Clothing' && (
                        <>
                          <li><strong>{language === 'tr' ? 'Materyal:' : 'Material:'}</strong> {language === 'tr' ? 'Pamuk/Polyester' : 'Cotton/Polyester'}</li>
                          <li><strong>{language === 'tr' ? 'Renk:' : 'Color:'}</strong> {language === 'tr' ? 'Görseldeki gibi' : 'As shown in image'}</li>
                          <li><strong>{language === 'tr' ? 'Bakım:' : 'Care:'}</strong> {language === 'tr' ? 'Etiket talimatlarına göre' : 'Follow label instructions'}</li>
                        </>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </TabContent>
          </TabsSection>

      <AnimatePresence>
          {isModalOpen && productData && (
            <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
              <ModalContent
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
              <ModalImage 
                  src={productData.images?.[selectedImage] || productData.image}
                  alt={productData.name}
                />
                <ModalCloseButton onClick={closeModal}>×</ModalCloseButton>
                {productData.images && productData.images.length > 1 && (
                  <>
                    <ModalPrevButton onClick={handlePrevImage}>‹</ModalPrevButton>
                    <ModalNextButton onClick={handleNextImage}>›</ModalNextButton>
                  </>
                )}
            </ModalContent>
            </ModalOverlay>
        )}
      </AnimatePresence>
      
      {/* Question Modal */}
      <AnimatePresence>
        {isQuestionModalOpen && productData && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsQuestionModalOpen(false)}
          >
            <QuestionModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalCloseButton onClick={() => setIsQuestionModalOpen(false)}>×</ModalCloseButton>
              
              <QuestionModalTitle>{language === 'tr' ? 'Ürün Hakkında Soru Sor' : 'Ask About Product'}</QuestionModalTitle>
              <QuestionModalSubtitle>{language === 'tr' ? 'Ürün:' : 'Product:'} {productData.name}</QuestionModalSubtitle>
              
              {questionSuccess ? (
                <SuccessMessage>
                  {language === 'tr' 
                    ? '✅ Sorunuz başarıyla gönderildi! En kısa sürede size dönüş yapılacaktır.' 
                    : '✅ Your question has been sent successfully! We will get back to you soon.'}
                </SuccessMessage>
              ) : (
                <QuestionForm onSubmit={handleQuestionSubmit}>
                  <FormGroup>
                    <FormLabel>{language === 'tr' ? 'Ad Soyad *' : 'Full Name *'}</FormLabel>
                    <FormInput
                      type="text"
                      value={questionForm.customer_name}
                      onChange={(e) => setQuestionForm({...questionForm, customer_name: e.target.value})}
                      required
                      placeholder={language === 'tr' ? 'Adınız ve soyadınız' : 'Your full name'}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel>Email *</FormLabel>
                    <FormInput
                      type="email"
                      value={questionForm.customer_email}
                      onChange={(e) => setQuestionForm({...questionForm, customer_email: e.target.value})}
                      required
                      placeholder="email@example.com"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel>{language === 'tr' ? 'Sorunuz *' : 'Your Question *'}</FormLabel>
                    <FormTextarea
                      value={questionForm.question}
                      onChange={(e) => setQuestionForm({...questionForm, question: e.target.value})}
                      required
                      placeholder={language === 'tr' ? 'Ürün hakkında sormak istediğiniz soruyu yazın...' : 'Write your question about the product...'}
                      rows={5}
                    />
                  </FormGroup>
                  
                  <SubmitButton type="submit" disabled={questionSubmitting}>
                    {questionSubmitting 
                      ? (language === 'tr' ? 'Gönderiliyor...' : 'Sending...') 
                      : (language === 'tr' ? 'Gönder' : 'Send')}
                  </SubmitButton>
                </QuestionForm>
              )}
            </QuestionModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
      </Container>
    </ProductDetailContainer>
  );
};

export default ProductDetail;
